export const EXPENSE_CATEGORY_MAP = {
  Janitorial: {},
  Landscaping: {},
  Elevators: {},
  HVAC: {},
  Security: {},
  Waste: {},
  Windows: {},
  Utilities: {},
  Other: {},
};

export type EXPENSE_CATEGORY_MAP = typeof EXPENSE_CATEGORY_MAP;

export type ExpenseCategory = keyof EXPENSE_CATEGORY_MAP;

export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  "Janitorial",
  "Landscaping",
  "Elevators",
  "HVAC",
  "Security",
  "Waste",
  "Windows",
  "Utilities",
  "Other",
];

export type BooleanQuestionPrompt = {
  readonly type: "booleanQuestion";
  readonly text: string;
};

export type NumericQuestionPrompt = {
  readonly type: "numericQuestion";
  readonly text: string;
  readonly unit: string;
  readonly displayUnit?: string;
  readonly prefix?: string;
};

export type QuestionPrompt = BooleanQuestionPrompt | NumericQuestionPrompt;

export type ExpenseQuestion = {
  readonly prompt: NumericQuestionPrompt;
  readonly priority: number;
  readonly categories: ExpenseCategory[];
};

export type MeasurementDefinition = {
  readonly label: string;
  readonly unit?: string;
  readonly bulkImportColumnName?: string;
  readonly question?: ExpenseQuestion;
};

const newMeasurement = (def: MeasurementDefinition) => {
  return Object.assign(def, {
    def: () => def,
    setBulkImportColumn: (name: string) =>
      newMeasurement({ ...def, bulkImportColumnName: name }),
    setQuestion: (
      prompt: NumericQuestionPrompt,
      priority: number,
      ...categories: ExpenseCategory[]
    ) =>
      newMeasurement({
        ...def,
        question: {
          prompt,
          priority,
          categories,
        },
      }),
  });
};

const measurement = (label: string, unit?: string) =>
  newMeasurement({
    label,
    unit,
  });

const sqftPrompt = (text: string): NumericQuestionPrompt => ({
  text,
  type: "numericQuestion",
  unit: "sq. ft.",
});

const ftPrompt = (text: string): NumericQuestionPrompt => ({
  text,
  type: "numericQuestion",
  unit: "ft.",
});

const yearPrompt = (text: string): NumericQuestionPrompt => ({
  text,
  type: "numericQuestion",
  displayUnit: "",
  unit: "year",
});

const countPrompt = (text: string): NumericQuestionPrompt => ({
  text,
  type: "numericQuestion",
  displayUnit: "",
  unit: "count",
});

export const TOTAL_BUILDING_MEASUREMENT = measurement(
  "Total Building",
  "sq. ft."
)
  .setBulkImportColumn("Total Building (sq. ft.)")
  .setQuestion(
    sqftPrompt("What is the total building size?"),
    4,
    ...EXPENSE_CATEGORIES
  );

export const PROPERTY_MEASUREMENTES: MeasurementDefinition[] = [
  measurement("Building Class").setBulkImportColumn("Building Class"),
  measurement("Sub Market").setBulkImportColumn("Sub Market"),
  measurement("Building Type").setBulkImportColumn("Building Type"),
  measurement("Number of Floors")
    .setBulkImportColumn("Number of Floors")
    .setQuestion(countPrompt("How many floors are there?"), 5, "Other"),
  measurement("Floor Plate", "sq. ft.")
    .setBulkImportColumn("Floor Plate (sq. ft.)")
    .setQuestion(
      sqftPrompt("What is the size of the floor plate?"),
      5,
      ...EXPENSE_CATEGORIES
    ),
  measurement("Total", "sq. ft.").setBulkImportColumn(
    "Property Total (sq. ft.)"
  ),
  TOTAL_BUILDING_MEASUREMENT,
  measurement("Year Constructed")
    .setBulkImportColumn("Year Constructed")
    .setQuestion(
      yearPrompt("What year was the building constructed?"),
      5,
      "Other"
    ),
  measurement("Retail Rentable Area", "sq. ft.")
    .setBulkImportColumn("Retail Rentable Area (sq. ft.)")
    .setQuestion(
      sqftPrompt("What is the size of the rentable retail area?"),
      5,
      ...EXPENSE_CATEGORIES
    ),

  measurement("Office Rentable Area", "sq. ft.")
    .setBulkImportColumn("Office Rentable Area (sq. ft.)")
    .setQuestion(
      sqftPrompt("What is the size of the rentable office area?"),
      5,
      ...EXPENSE_CATEGORIES
    ),
  measurement("Flex / Other Area", "sq. ft.")
    .setBulkImportColumn("Flex / Other Area (sq. ft.)")
    .setQuestion(
      sqftPrompt("What is the size of the flex area?"),
      5,
      ...EXPENSE_CATEGORIES
    ),
  measurement("Occupancy", "%").setBulkImportColumn("Occupancy (%)"),
  measurement("Facade").setBulkImportColumn("Facade"),
  measurement("Windows").setBulkImportColumn("Windows"),
  measurement("Roof Type").setBulkImportColumn("Roof Type"),
  measurement("Roof Installation Year")
    .setBulkImportColumn("Roof Installation Year")
    .setQuestion(yearPrompt("What year was the roof installed?"), 5, "Other"),
  measurement("# of Restrooms per Floor")
    .setBulkImportColumn("# of Restrooms per Floor")
    .setQuestion(countPrompt("How many restrooms per floor?"), 5, "Janitorial"),
  measurement("# of Elevators")
    .setBulkImportColumn("# of Elevators")
    .setQuestion(countPrompt("How many elevators are there?"), 5, "Elevators"),
  measurement("Prevalent Elevator Type").setBulkImportColumn(
    "Prevalent Elevator Type"
  ),
  measurement("# of Escalators")
    .setBulkImportColumn("# of Escalators")
    .setQuestion(countPrompt("How many escalators are there?"), 5, "Elevators"),
  measurement("HVAC Distribution").setBulkImportColumn("HVAC Distribution"),
  measurement("Parking Type").setBulkImportColumn("Parking Type"),
  measurement("Parking Spaces")
    .setBulkImportColumn("Parking Spaces")
    .setQuestion(
      countPrompt("How many parking spaces are there?"),
      5,
      "Landscaping"
    ),
  measurement("# of Entrances")
    .setBulkImportColumn("# of Entrances")
    .setQuestion(countPrompt("How many entrances are there?"), 5, "Other"),
  measurement("Ceiling Height", "ft.")
    .setBulkImportColumn("Ceiling Height (ft.)")
    .setQuestion(ftPrompt("How high are the ceilings?"), 5, "Other"),
  measurement("Deck Height", "ft.").setBulkImportColumn("Deck Height (ft.)"),
  measurement("Truck / Rail Access").setBulkImportColumn("Truck / Rail Access"),
  measurement("Bay Areas").setBulkImportColumn("Bay Areas"),
  measurement("Full-Height Bay Area").setBulkImportColumn(
    "Full-Height Bay Area"
  ),
  measurement("Common Area Finishes").setBulkImportColumn(
    "Common Area Finishes"
  ),
  measurement("Lobby / Atrium Size").setBulkImportColumn("Lobby / Atrium Size"),
  measurement("Lobby / Atrium Finishes").setBulkImportColumn(
    "Lobby / Atrium Finishes"
  ),
  measurement("Corridor Finishes").setBulkImportColumn("Corridor Finishes"),
  measurement("Restroom Finishes").setBulkImportColumn("Restroom Finishes"),
  measurement("Tenant Finishes").setBulkImportColumn("Tenant Finishes"),
  measurement("Sprinkler System").setBulkImportColumn("Sprinkler System"),
  measurement("Loading Dock").setBulkImportColumn("Loading Dock"),
  measurement("Back-up Power").setBulkImportColumn("Back-up Power"),
  measurement("Property ID").setBulkImportColumn("Property ID (Optional)"),
];

export type ExpenseCommonDefinition = {
  readonly name: string;
  readonly bulkImportColumnName?: string;
  readonly bulkImportColumn?: string;
  readonly question?: ExpenseQuestion;
};

export type ExpenseDetailDefinition = ExpenseCommonDefinition & {
  readonly breakdown?: never;
};

export type ExpenseDefinition = ExpenseCommonDefinition & {
  readonly breakdown?: ExpenseDetailDefinition[];
};

const newExpense = (def: ExpenseDefinition) => {
  return Object.assign(def, {
    setBulkImportColumn: (name: string, column?: string) =>
      newExpense({
        ...def,
        bulkImportColumnName: name,
        bulkImportColumn: column,
      }),
    addBreakdown: (breakdown: ExpenseDetailDefinition[]) =>
      newExpense({
        ...def,
        breakdown,
      }),
    setQuestion: (
      prompt: NumericQuestionPrompt,
      priority: number,
      ...categories: ExpenseCategory[]
    ) =>
      newExpense({
        ...def,
        question: {
          prompt,
          priority,
          categories,
        },
      }),
  });
};

const newExpenseDetail = (def: ExpenseDetailDefinition) => {
  return Object.assign(def, {
    setBulkImportColumn: (name: string, column?: string) =>
      newExpenseDetail({
        ...def,
        bulkImportColumnName: name,
        bulkImportColumn: column,
      }),
    setQuestion: (
      prompt: NumericQuestionPrompt,
      priority: number,
      ...categories: ExpenseCategory[]
    ) =>
      newExpenseDetail({
        ...def,
        question: {
          prompt,
          priority,
          categories,
        },
      }),
  });
};

const expense = (name: string) =>
  newExpense({
    name,
  });

const expenseDetail = (name: string) =>
  newExpenseDetail({
    name,
  });

const annualSpend = (text: string): NumericQuestionPrompt => ({
  text,
  type: "numericQuestion",
  prefix: "$",
  unit: "/ year",
});

export const LANDSCAPING_EXPENSES = expense("Grounds Maintenance")
  .setBulkImportColumn("Grounds Maintenance ($/year)")
  .setQuestion(
    annualSpend("How much do you spend on grounds maintenance each year?"),
    1,
    "Landscaping"
  )
  .addBreakdown([
    expenseDetail("Maintenance").setBulkImportColumn(
      "Landscape Maintenance ($/year)"
    ),
    expenseDetail("Enhancements").setBulkImportColumn(
      "Landscape Enhancements ($/year)"
    ),
    expenseDetail("Snow Removal").setBulkImportColumn("Snow Removal ($/year)"),
  ]);

export const JANITORIAL_EXPENSES = expense("Janitorial")
  .setBulkImportColumn("Janitorial ($/year)")
  .setQuestion(
    annualSpend("How much do you spend on janitorial expenses each year?"),
    1,
    "Janitorial"
  )
  .addBreakdown([
    expenseDetail("Routine")
      .setBulkImportColumn("Routine Contract ($/year)")
      .setQuestion(
        annualSpend(
          "How much of your janitorial expense is the routine contract?"
        ),
        1,
        "Janitorial"
      ),
    expenseDetail("Special").setBulkImportColumn("Special Contract ($/year)"),
    expenseDetail("Miscellaneous").setBulkImportColumn(
      "Miscellaneous ($/year)"
    ),
    expenseDetail("Interior Trash Removal").setBulkImportColumn(
      "Interior Trash Removal + Recycling ($/year)"
    ),
  ]);

export const WASTE_EXPENSES = expense("Exterior Waste")
  .setBulkImportColumn("Exterior Waste / Trash Removal ($/year)")
  .setQuestion(
    annualSpend("How much do you spend on exterior waste each year?"),
    1,
    "Waste"
  );

export const WINDOWS_EXPENSES = expense("Window Cleaning")
  .setBulkImportColumn("Window Cleaning ($/year)")
  .setQuestion(
    annualSpend("How much do you spend on window cleaning each year?"),
    1,
    "Windows"
  );

export const UTILITY_EXPENSES = expense("Utilities")
  .setBulkImportColumn("Utilities ($/year)")
  .setQuestion(
    annualSpend("How much do you spend on utilities each year?"),
    1,
    "Utilities"
  )
  .addBreakdown([
    expenseDetail("Electricity").setBulkImportColumn(
      "Electricity Cost ($/year)"
    ),
    expenseDetail("Water & Sewer").setBulkImportColumn(
      "Water + Sewer Cost ($/year)"
    ),
    expenseDetail("Irrigation").setBulkImportColumn(
      "Water - Irrigation ($/year)"
    ),
    expenseDetail("Telephone / Internet").setBulkImportColumn(
      "Telephone / Internet ($/year)"
    ),
  ]);

export const SECURITY_EXPENSES = expense("Security")
  .setBulkImportColumn("Security ($/year)")
  .setQuestion(
    annualSpend("How much do you spend on security each year?"),
    1,
    "Security"
  )
  .addBreakdown([
    expenseDetail("Pysical").setBulkImportColumn("Physical Security ($/year)"),
    expenseDetail("System Monitoring").setBulkImportColumn(
      "System Monitoring ($/year)"
    ),
    expenseDetail("Equipment").setBulkImportColumn("Equipment Spend ($/year)"),
  ]);

export const ELEVATOR_EXPENSES = expense("Elevators")
  .setBulkImportColumn("Elevators ($/year)", "Q")
  .setQuestion(
    annualSpend("How much do you spend on elevators each year?"),
    1,
    "Elevators"
  )
  .addBreakdown([
    expenseDetail("Maintenance").setBulkImportColumn(
      "Maintenance ($/year)",
      "R"
    ),
    expenseDetail("Repairs").setBulkImportColumn("Repairs ($/year)", "S"),
  ]);

export const HVAC_EXPENSES = expense("HVAC")
  .setBulkImportColumn("HVAC ($/year)", "T")
  .setQuestion(
    annualSpend("How much do you spend on HVAC each year?"),
    1,
    "HVAC"
  )
  .addBreakdown([
    expenseDetail("Maintenance").setBulkImportColumn(
      "Maintenance ($/year)",
      "U"
    ),
    expenseDetail("Repairs").setBulkImportColumn("Repairs ($/year)", "V"),
  ]);

export const OTHER_EXPENSES = expense("Other")
  .setBulkImportColumn("Other")
  .addBreakdown([
    expenseDetail("Interior Plant Maintenance").setBulkImportColumn(
      "Interior Plant Maintenance ($/year)"
    ),

    expenseDetail("Electrical Repairs").setBulkImportColumn(
      "Electrical Repairs ($/year)"
    ),
    expenseDetail("Roof Repairs").setBulkImportColumn(
      "Roof Repairs ($/replace)"
    ),
    expenseDetail("Plumbing Repairs").setBulkImportColumn(
      "Plumbing Repairs ($/year)"
    ),
    expenseDetail("Fire / Life Safety Repairs").setBulkImportColumn(
      "Fire / Life Safety Repairs ($/year)"
    ),
    expenseDetail("Power Washing + Parking Lot Sweeping").setBulkImportColumn(
      "Power Washing + Parking Lot Sweeping ($/year)"
    ),
    expenseDetail("Management Fee").setBulkImportColumn(
      "Management Fee ($/year)"
    ),
    expenseDetail("General & Admin").setBulkImportColumn(
      "General & Admin ($/year)"
    ),
    expenseDetail("Insurance").setBulkImportColumn("Insurance ($/year)"),
    expenseDetail("Taxes").setBulkImportColumn("Taxes ($/year)"),
  ]);

export const EXPENSES: ExpenseDefinition[] = [
  LANDSCAPING_EXPENSES,
  JANITORIAL_EXPENSES,
  WASTE_EXPENSES,
  WINDOWS_EXPENSES,
  UTILITY_EXPENSES,
  SECURITY_EXPENSES,
  ELEVATOR_EXPENSES,
  HVAC_EXPENSES,
  OTHER_EXPENSES,
];

export const AnnualExpenseByCategory: {
  [category in ExpenseCategory]: ExpenseDefinition;
} = {
  Elevators: ELEVATOR_EXPENSES,
  HVAC: HVAC_EXPENSES,
  Janitorial: JANITORIAL_EXPENSES,
  Landscaping: LANDSCAPING_EXPENSES,
  Security: SECURITY_EXPENSES,
  Utilities: UTILITY_EXPENSES,
  Waste: WASTE_EXPENSES,
  Windows: WINDOWS_EXPENSES,
  Other: OTHER_EXPENSES,
};
